import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/axios';

export const loginAdmin = createAsyncThunk(
  'admin/login',
  async (credentials, thunkAPI) => {
    try {
      const response = await axiosInstance.post('/admin/login', credentials);
      // Store token and user data on successful login
      localStorage.setItem('token', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data.admin));
      return response.data;
    } catch (error) {
      // Check if it's a 402 status code which indicates password update required
      if (error.response?.status === 402) {
        // Return an object that indicates a password update is required
        // Also store any temporary token provided for the password update
        console.error('Password update required:', error.response.data);
        return thunkAPI.fulfillWithValue({
          requirePasswordUpdate: true,
          temporaryToken: error.response.data?.data.temporaryToken || null,
          message: error.response?.data?.message || 'Password update required'
        });
      }
      return thunkAPI.rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

export const createAdmin = createAsyncThunk(
    'admin/signup',
    async (credentials, thunkAPI) => {
        try {
          const response = await axiosInstance.post('/admin/signup', {
            ...credentials
          });
          return response.data;
        } catch (error) {
          return thunkAPI.rejectWithValue(error.response?.data?.message || 'admin signup failed');
        }
      }
  );
export const editAdmin = createAsyncThunk('admin/edit', async (credentials, thunkAPI) => {
    try {
        const response = await axiosInstance.put(`/admin/${credentials.id}`, {
            ...credentials
        });
        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error.response?.data?.message || 'admin update failed');
    }
});

export const getAllAdmin = createAsyncThunk(
    'admin/getAll',
    async (_, thunkAPI) => {
        try {
          const response = await axiosInstance.get('/admin');
          console.log("Response from getAllAdmin:", response.data);
          return response.data;
        } catch (error) {
          return thunkAPI.rejectWithValue(error.response?.data?.message || 'Failed to fetch admin');
        }
      }
  );
export const adminStatusUpdate = createAsyncThunk(
    'admin/status',
    async (credentials, thunkAPI) => {
        try {
          const response = await axiosInstance.put(`/admin/status/${credentials.id}`, {
            ...credentials
          });
          return response.data;
        } catch (error) {
          return thunkAPI.rejectWithValue(error.response?.data?.message || 'Failed to fetch admin');
        }
      }
  );  
  export const adminDelete = createAsyncThunk(
    'admin/delete',
    async (credentials, thunkAPI) => {
        try {
          const response = await axiosInstance.delete(`/admin/${credentials.id}`);
          // Store the ID in the response for use in the reducer
          return { ...response.data, deleteId: credentials.id };
        } catch (error) {
          return thunkAPI.rejectWithValue(error.response?.data?.message || 'Failed to delete admin');
        }
      }
  );
  export const adminResetPassword = createAsyncThunk(
    'admin/resetPassword',
    async (credentials, thunkAPI) => {
        try {
          const response = await axiosInstance.post(`/admin/reset-password`, {
            ...credentials
          });
          return response.data;
        } catch (error) {
          return thunkAPI.rejectWithValue(error.response?.data?.message || 'Failed to reset admin password');        }
      }
  );
const adminSlice = createSlice({
  name: "admin", // Changed from "adminState" to match the store definition
  initialState: {
    admins: [], // Make sure this is defined as an array
    adminLoading: false,
    error: null,
    user: JSON.parse(localStorage.getItem('user')) || null,
    token: localStorage.getItem('token') || null,
    isAuthenticated: !!localStorage.getItem('token'),
    passwordUpdateRequired: false,
    temporaryToken: null
  },
  reducers: {
     logout: (state) => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      state.passwordUpdateRequired = false;
      state.temporaryToken = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      localStorage.setItem('user', JSON.stringify(action.payload));
    },
    resetPasswordUpdateRequired: (state) => {
      state.passwordUpdateRequired = false;
      state.temporaryToken = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Create admin
      .addCase(createAdmin.pending, (state) => {
        state.adminLoading = true;
        state.error = null;
      })
      .addCase(createAdmin.fulfilled, (state, action) => {
        state.adminLoading = false;
        // Use state.admins (plural) instead of state.admin
        state.admins.push(action.payload.data);
      })
      .addCase(createAdmin.rejected, (state, action) => {
        state.adminLoading = false;
        state.error = action.payload || action.error.message;
      })
      
      // Get all admins
      .addCase(getAllAdmin.pending, (state) => {
        state.adminLoading = true;
        state.error = null;
      })      .addCase(getAllAdmin.fulfilled, (state, action) => {
        state.adminLoading = false;
        // Detailed logging to debug the issue
        console.log("getAllAdmin fulfilled - Full payload:", action.payload);
        console.log("getAllAdmin fulfilled - action.payload.data:", action.payload.data);
        console.log("getAllAdmin fulfilled - typeof action.payload.data:", typeof action.payload.data);
        console.log("getAllAdmin fulfilled - Is Array:", Array.isArray(action.payload.data));
        
        // Make sure we're setting an array or falling back to empty array
        state.admins = Array.isArray(action.payload.data) ? action.payload.data : [];
      })
      .addCase(getAllAdmin.rejected, (state, action) => {
        state.adminLoading = false;
        state.error = action.payload || action.error.message;
      })      // Edit admin
      .addCase(editAdmin.pending, (state) => {
        state.adminLoading = true;
        state.error = null;
      })
      .addCase(editAdmin.fulfilled, (state, action) => {
        state.adminLoading = false;
        // Update the admins array with the edited admin
        const index = state.admins.findIndex(admin => admin.id === action.payload.data.id);
        if (index !== -1) {
          state.admins[index] = action.payload.data;
        }
      })
      .addCase(editAdmin.rejected, (state, action) => {
        state.adminLoading = false;
        state.error = action.payload || action.error.message;
      })        // Admin status update
      .addCase(adminStatusUpdate.pending, (state) => {
        state.adminLoading = true;
        state.error = null;
      })
      .addCase(adminStatusUpdate.fulfilled, (state, action) => {
        state.adminLoading = false;
        // If we want to update the admin array with the edited admin
        const index = state.admins.findIndex(admin => admin.id === action.payload.data.id);
        if (index !== -1) {
          state.admins[index] = action.payload.data;
        }
      })
      .addCase(adminStatusUpdate.rejected, (state, action) => {
        state.adminLoading = false;
        state.error = action.payload || action.error.message;
      })    //   Admin delete
    .addCase(adminDelete.pending, (state) => {
      state.adminLoading = true;
      state.error = null;
    })    
    .addCase(adminDelete.fulfilled, (state, action) => {
      state.adminLoading = false;
      console.log("adminDelete.fulfilled - Full payload:", action.payload);
      
      // Use deleteId from the action that we added in the thunk
      const idToDelete = action.payload.deleteId;
      console.log("adminDelete.fulfilled - ID to delete:", idToDelete);
      
      // Remove the deleted admin from the admins array
      state.admins = state.admins.filter(admin => admin.id !== idToDelete);
    })
    .addCase(adminDelete.rejected, (state, action) => {
      state.adminLoading = false;
      state.error = action.payload || action.error.message;
    })

    // Admin reset password
    .addCase(adminResetPassword.pending, (state) => {
      state.adminLoading = true;
      state.error = null;
    })
    .addCase(adminResetPassword.fulfilled, (state, action) => {
      state.adminLoading = false;
      // Handle successful password reset if needed
    })    .addCase(adminResetPassword.rejected, (state, action) => {
      state.adminLoading = false;
      state.error = action.payload || action.error.message;
    })
      // Login admin cases
    .addCase(loginAdmin.pending, (state) => {
      state.adminLoading = true;
      state.error = null;
      state.passwordUpdateRequired = false;
      state.temporaryToken = null;
    })
    .addCase(loginAdmin.fulfilled, (state, action) => {
      state.adminLoading = false;
      
      // Check if password update is required
      if (action.payload.requirePasswordUpdate) {
        // Handle password update requirement
        state.passwordUpdateRequired = true;
        state.temporaryToken = action.payload.temporaryToken;
        state.error = action.payload.message;
        state.isAuthenticated = false;
      } else {
        // Normal successful login
        state.user = action.payload.admin;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.error = null;
        state.passwordUpdateRequired = false;
        state.temporaryToken = null;
      }
    })
    .addCase(loginAdmin.rejected, (state, action) => {
      state.adminLoading = false;
      state.error = action.payload || action.error.message;
      state.passwordUpdateRequired = false;
      state.temporaryToken = null;
    });

  }
});
export const { logout, clearError, setUser, resetPasswordUpdateRequired } = adminSlice.actions;
export default adminSlice.reducer;